import { Dropdown, Table } from "antd";
import { getInitials } from "helpers/initials";
import { ColumnProps } from "antd/lib/table";
import { IntegrateStatusEnum, UserRoleEnum } from "config/enum";
import {
  DORMITORY_DETAIL_ROUTE,
  END_USER_DETAIL_ROUTE,
  HOME_CONTROLLER_MASTER_ROUTE,
  HOME_CONTROLLER_ROUTE,
} from "config/route-consts";
import { Device } from "models/Device";
import { DeviceType } from "models/DeviceType";
import { FirmwareIntegration } from "models/FirmwareIntegration/FirmwareIntegration";
import { HomeController } from "models/HomeController";
import moment, { Moment } from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { homeControllerRepository } from "repositories/home-controller-repository";
import { authService } from "services/auth-services";
import detailService from "services/pages/detail-service";
import {
  HomeControllerCommandModal,
  useHomeControllerCommandModal,
} from "./HomeControllerCommandModal";
import "./HomeControllerDetail.scss";
import {
  HomeControllerLogModal,
  useHomeControllerLogModal,
} from "./HomeControllerLogModal";
import {
  DeviceDetailModal,
  useDeviceDetailModal,
} from "./DeviceDetailModal";

function HomeControllerDetail() {
  const [translate] = useTranslation();
  const history = useHistory();

  const { userRole } = authService.useRole();

  const { model } = detailService.useDetail(
    HomeController,
    homeControllerRepository.get,
    undefined,
    HOME_CONTROLLER_ROUTE
  );

  const handleGoDormitory = React.useCallback(
    (id: string | number) => (_event: any) => {
      if (id) {
        history.push(`${DORMITORY_DETAIL_ROUTE}?id=${id}`);
      }
    },
    [history]
  );

  const handleGoEndUser = React.useCallback(
    (id: string | number) => (_event: any) => {
      if (id) {
        history.push(`${END_USER_DETAIL_ROUTE}?id=${id}`);
      }
    },
    [history]
  );

  const {
    list: hcLogList,
    loading,
    visibleHomeControllerLogModal,
    openHomeControllerLogModal,
    cancelHomeControllerLogModal,
  } = useHomeControllerLogModal(model, homeControllerRepository.listLogs);

  const {
    visibleHomeControllerCommandModal,
    openHomeControllerCommandModal,
    cancelHomeControllerCommandModal,
  } = useHomeControllerCommandModal();

  const {
    visible: visibleDeviceDetailModal,
    selectedDevice,
    openDeviceDetailModal,
    closeDeviceDetailModal,
  } = useDeviceDetailModal();

  // Reset HC và VPN chưa có xử lý trong code cũ (chỉ là chữ). Để sẵn chỗ nối logic.
  const handleResetHomeController = React.useCallback(() => {}, []);
  const handleOpenVpn = React.useCallback(() => {}, []);

  // Thiết bị con trực tiếp của HC (giữ đúng điều kiện lọc của bản cũ).
  const devices = React.useMemo(
    () =>
      (model?.devices ?? []).filter(
        (item) => item.parentId === "00000000-0000-0000-0000-000000000000"
      ),
    [model]
  );

  const deviceColumns: ColumnProps<Device>[] = [
    {
      title: "Thiết bị",
      key: "name",
      dataIndex: "name",
      ellipsis: true,
      render(name: string, device: Device) {
        return (
          <div className="ant-cell-master__container">
            <div className="cell-master__first-row hc-detail__truncate" title={name}>
              {name}
            </div>
            <div className="cell-master__second-row tabular-nums">{device.macAddress}</div>
          </div>
        );
      },
    },
    {
      title: "Nhóm",
      key: "deviceType",
      dataIndex: "deviceType",
      width: 140,
      render(deviceType: DeviceType) {
        return deviceType?.name || <span className="text-muted-token">—</span>;
      },
    },
    {
      title: "ID thiết bị",
      key: "id",
      dataIndex: "id",
      width: 240,
      render(id: string) {
        return (
          <span className="hc-detail__mono hc-detail__truncate" title={id}>
            {id}
          </span>
        );
      },
    },
    {
      title: "Phiên bản FW",
      key: "firmwareVersion",
      dataIndex: "firmwareVersion",
      width: 120,
      render(firmwareVersion: string) {
        return firmwareVersion ? (
          <span className="tabular-nums">{firmwareVersion}</span>
        ) : (
          <span className="text-muted-token">—</span>
        );
      },
    },
  ];

  const owner = model.dormitory?.endUser;
  const firmwareIntegrations: FirmwareIntegration[] = model?.firmwareIntegrations ?? [];
  const createdAt = model.createdAt ? moment(model.createdAt as Moment) : null;

  const moreMenu = (
    <div className="hc-detail__menu">
      <button type="button" onClick={handleOpenVpn}>
        <i className="tio-security_on_outlined" />
        VPN
      </button>
      <button type="button" className="hc-detail__menu-danger" onClick={handleResetHomeController}>
        <i className="tio-replay" />
        Reset HC
      </button>
    </div>
  );

  return (
    <>
      <div className="page hc-detail">
        {/* 3. Đầu trang: tên, trạng thái, định danh, thao tác */}
        <div className="hc-detail__head">
          <div className="hc-detail__head-text">
            <Link to={HOME_CONTROLLER_MASTER_ROUTE} className="page-back">
              <i className="tio-chevron_left" aria-hidden />
              Danh sách HC
            </Link>
            <h1 className="hc-detail__title">
              {model.name}
              {model.homeControllerActiveStatus && (
                <span
                  className={`home-controller__status home-controller__status--${model.homeControllerActiveStatusId}`}
                >
                  {model.homeControllerActiveStatus.name}
                </span>
              )}
            </h1>
            <p className="hc-detail__meta tabular-nums">
              MAC {model.macAddress || "—"} · IP {model.ipAddress || "—"} · FW{" "}
              {model.version || "—"}
            </p>
          </div>
          <div className="hc-detail__actions">
            <button type="button" className="hc-detail__btn" onClick={openHomeControllerLogModal}>
              <i className="tio-document_text_outlined" />
              Xem log
            </button>
            <button
              type="button"
              className="hc-detail__btn hc-detail__btn--primary"
              onClick={openHomeControllerCommandModal}
            >
              <i className="tio-remote_controller" />
              Điều khiển từ xa
            </button>
            {userRole.includes(UserRoleEnum.Admin) && (
              <Dropdown overlay={moreMenu} trigger={["click"]} placement="bottomRight">
                <button
                  type="button"
                  className="hc-detail__btn hc-detail__btn--icon"
                  aria-label="Thêm thao tác"
                >
                  <i className="tio-more_horizontal" />
                </button>
              </Dropdown>
            )}
          </div>
        </div>

        {/* 4. Chủ nhà · Nhà · Ngày tạo */}
        <section className="hc-detail__card hc-detail__strip">
          {owner ? (
            <button
              type="button"
              className="hc-detail__link-cell"
              onClick={handleGoEndUser(model.dormitory?.endUserId)}
            >
              {owner.avatar ? (
                <img className="hc-detail__avatar" src={owner.avatar} alt="" />
              ) : (
                <span className="hc-detail__avatar hc-detail__avatar--initials">
                  {getInitials(owner.displayName)}
                </span>
              )}
              <span className="hc-detail__link-text">
                <span className="hc-detail__label">Chủ nhà</span>
                <span className="hc-detail__link-title">{owner.displayName || "Không tên"}</span>
                <span className="hc-detail__link-sub">{owner.email || "—"}</span>
              </span>
              <i className="tio-chevron_right hc-detail__chevron" />
            </button>
          ) : (
            <div className="hc-detail__link-cell">
              <span className="hc-detail__avatar hc-detail__avatar--icon">
                <i className="tio-user_outlined" />
              </span>
              <span className="hc-detail__link-sub">Chưa gắn với nhà nào</span>
            </div>
          )}
          {model.dormitory ? (
            <button
              type="button"
              className="hc-detail__link-cell"
              onClick={handleGoDormitory(model.dormitoryId)}
            >
              <span className="hc-detail__avatar hc-detail__avatar--icon">
                <i className="tio-home_outlined" />
              </span>
              <span className="hc-detail__link-text">
                <span className="hc-detail__label">Nhà</span>
                <span className="hc-detail__link-title">{model.dormitory.name || "Không tên"}</span>
                <span className="hc-detail__link-sub" title={model.dormitory.address}>
                  {model.dormitory.address || "—"}
                </span>
              </span>
              <i className="tio-chevron_right hc-detail__chevron" />
            </button>
          ) : (
            <div className="hc-detail__link-cell">
              <span className="hc-detail__avatar hc-detail__avatar--icon">
                <i className="tio-home_outlined" />
              </span>
              <span className="hc-detail__link-sub">—</span>
            </div>
          )}
          <div className="hc-detail__stat">
            <span className="hc-detail__label">Ngày tạo</span>
            <span className="hc-detail__stat-value tabular-nums">
              {createdAt ? createdAt.format("DD/MM/YYYY") : "—"}
            </span>
            {createdAt && <span className="hc-detail__link-sub">{createdAt.fromNow()}</span>}
          </div>
        </section>

        <div className="hc-detail__grid">
          {/* 5. Thiết bị */}
          <section className="hc-detail__card">
            <div className="hc-detail__card-head">
              <h2 className="hc-detail__card-title">
                Thiết bị <span className="hc-detail__count">· {devices.length}</span>
              </h2>
            </div>
            {devices.length === 0 ? (
              <p className="hc-detail__empty">Chưa có thiết bị nào kết nối với HC này</p>
            ) : (
              <>
                <div className="hc-detail__device-table">
                  <Table
                    rowKey={"id"}
                    columns={deviceColumns}
                    pagination={false}
                    dataSource={devices}
                    rowSelection={null}
                    tableLayout="fixed"
                    onRow={(record) => ({
                      onClick: () => openDeviceDetailModal(record),
                      style: { cursor: "pointer" },
                    })}
                  />
                </div>
                <ul className="hc-detail__rows hc-detail__device-rows">
                  {devices.map((device) => (
                    <li
                      key={device.id}
                      onClick={() => openDeviceDetailModal(device)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="hc-detail__row-text">
                        <div className="hc-detail__row-title hc-detail__truncate">{device.name}</div>
                        <div className="hc-detail__link-sub">
                          {device.deviceType?.name || "—"} · FW {device.firmwareVersion || "—"}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          <div className="hc-detail__side">
            {/* 6. Thông số (MAC, IP, FW đã nằm ở đầu trang) */}
            <section className="hc-detail__card">
              <div className="hc-detail__card-head">
                <h2 className="hc-detail__card-title">Thông số</h2>
              </div>
              <dl className="hc-detail__dl">
                <div>
                  <dt>{translate("homeController.timeZone")}</dt>
                  <dd>
                    {model.timeZone == null
                      ? "—"
                      : model.timeZone >= 0
                      ? `GMT +${model.timeZone}`
                      : `GMT ${model.timeZone}`}
                  </dd>
                </div>
                <div>
                  <dt>Tự cập nhật FW</dt>
                  <dd>{model.autoUpdate == null ? "—" : model.autoUpdate ? "Bật" : "Tắt"}</dd>
                </div>
              </dl>
            </section>

            {/* 7. Firmware tích hợp */}
            <section className="hc-detail__card">
              <div className="hc-detail__card-head">
                <h2 className="hc-detail__card-title">
                  Firmware tích hợp{" "}
                  <span className="hc-detail__count">· {firmwareIntegrations.length}</span>
                </h2>
              </div>
              {firmwareIntegrations.length === 0 ? (
                <p className="hc-detail__empty">HC chưa có firmware tích hợp nào</p>
              ) : (
                <ul className="hc-detail__rows">
                  {firmwareIntegrations.map((integration, index) => {
                    const isInstalled =
                      integration.integrationStatusId === IntegrateStatusEnum.Installed;
                    return (
                      <li key={integration.id ?? index}>
                        <div className="hc-detail__row-text">
                          <div className="hc-detail__row-title hc-detail__truncate">
                            {integration.firmware?.name || "—"}
                          </div>
                          <div className="hc-detail__link-sub tabular-nums">
                            Phiên bản {integration.firmware?.version || "—"}
                          </div>
                        </div>
                        <span className={`status-badge${isInstalled ? " status-badge--success" : ""}`}>
                          {isInstalled ? "Đã cài đặt" : "Chưa cài đặt"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>
        </div>
      </div>
      <HomeControllerLogModal
        list={hcLogList}
        loading={loading}
        visibleHomeControllerLogModal={visibleHomeControllerLogModal}
        cancelHomeControllerLogModal={cancelHomeControllerLogModal}
      />

      <HomeControllerCommandModal
        model={model}
        visibleHomeControllerCommandModal={visibleHomeControllerCommandModal}
        cancelHomeControllerCommandModal={cancelHomeControllerCommandModal}
      />

      <DeviceDetailModal
        device={selectedDevice}
        visible={visibleDeviceDetailModal}
        onClose={closeDeviceDetailModal}
      />
    </>
  );
}


export default HomeControllerDetail;
